/*
 * Card renderer  ·  assets/hoh-card-render.js
 *
 * Why this lives here
 * -------------------
 * sections/hall-of-heroes.liquid sits a hair under Shopify's 256 KB
 * limit. The main-grid card builder is the largest single block in
 * renderGallery (~1500 bytes of template literal). Pulling it out
 * frees room for the raffle UI, the ticket icon, and future surfaces
 * without touching the section.
 *
 * Exposes:
 *   window.hohBuildCardHtml(c, i, newId)
 *
 * Output is BYTE-IDENTICAL to the previous inline template, plus the
 * new ticket count pill that sits next to the heart count. The pill
 * uses the rainbow-sweep ticket icon defined in hall-of-heroes.css.
 *
 * Helpers it depends on (all read off window):
 *   esc, getHeartCount, heartTierClass, hohGetRarity, placeholderBg,
 *   fmtMine, hohImgFallback (referenced via inline onerror), hohHeart,
 *   hohOpenDetail, hohOpenRules
 */
(function(){
  'use strict';

  /* Tiny resilient escape. Falls back to a textNode replace if the
     section's own esc() isn't on window for any reason. */
  function safeEsc(s){
    if(typeof window.esc === 'function') return window.esc(s);
    if(s == null) return '';
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }
  /* R558: UUIDs are the normal case, but a renderer should not assume an id
     can never contain a quote. Card ids are embedded in the small inline
     activation handlers below, so escape them for a single-quoted JS string
     independently from the HTML escaping used for labels. */
  function safeJsString(s){
    return String(s == null ? '' : s)
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
      .replace(/[\r\n\u2028\u2029]/g, ' ')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  /* BYLINE (Jan, 2026-10-20: "if its by Ashley owens, just make it by Ashley"):
     a FramedFantasy portrait's byline is the client's first name only. A
     player-submitted hero keeps the name the player typed. The detail modal
     reads the same helper (hoh-section-core.js). */
  function hohBylineName(c){
    var n = String((c && c.player_name) || '').trim();
    if(!n || !(c && c.is_ff_portrait)) return n;
    return n.split(/\s+/)[0];
  }
  window.hohBylineName = hohBylineName;
  function getHC(id){
    return (typeof window.getHeartCount === 'function') ? window.getHeartCount(id) : 0;
  }
  function htClass(hc){
    return (typeof window.heartTierClass === 'function') ? window.heartTierClass(hc) : '';
  }
  function rarityFor(hearts){
    if(typeof window.hohGetRarity === 'function') return window.hohGetRarity(hearts || 0);
    return { tier: 'common' };
  }
  /* R795: a tile's border is the hero's Hall tier (hohHallTier in
     hoh-section-core.js): the hero page score's stored tier, or Mythic for a
     FramedFantasy portrait, once the roster carries hero_rarity; today's
     legacy-hearts tier until sql/R795B answers. */
  function rarityForHero(c){
    if(typeof window.hohHallTier === 'function') return window.hohHallTier(c);
    return rarityFor(c && c.hearts);
  }
  function plBg(name){
    return (typeof window.placeholderBg === 'function') ? window.placeholderBg(name) : '#0a0a0f';
  }
  function fmtM(n){
    return (typeof window.fmtMine === 'function') ? window.fmtMine(n) : String(n);
  }

  /* Cards in the main grid intentionally do NOT show the ticket count.
     Tickets live inside the detail modal where there's room to explain
     odds. Signed-in cards use hearts as the at-a-glance stat; anonymous
     cards name the rarity tier without exposing the heart economy. */

  /* R173: shared badge-strip builder. Used by the card template AND by the
     god-mode grant path, which injects the strip into the live card node in
     place. Building the whole card fresh would drop its .hoh-visible reveal
     class (cards start at opacity:0 and only fade in once the scroll observer
     tags them), so the grant updates only this strip and leaves the node be.
     Returns '' when the hero has no earned trials, so non-quest heroes show
     nothing. Each chip carries data-badge-key only; the flavored tooltip is
     rendered on document.body at hover time (see the IIFE below) so the card's
     overflow:hidden can never clip it. aria-label keeps it accessible and a tap
     on touch opens the detail Trials band. */
  window.hohBuildBadgeStrip = function(c){
    try {
      /* R197: badges are a signed-in feature. Signed-out visitors see clean cards
         with no badges, so badges stay an incentive to submit a hero and sign in. */
      if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()) return '';
      var heroBadges = (typeof window.hohGetHeroBadges === 'function') ? window.hohGetHeroBadges(c) : [];
      if(!heroBadges.length) return '';
      /* R202: re-assert the rarity sort so the 5 shown chips are always the rarest
         first (mythic, gold, silver, bronze), regardless of upstream order. */
      if(typeof window.hohOrderBadges === 'function'){ try{ heroBadges = window.hohOrderBadges(heroBadges); }catch(_){} }
      var reg = window.HOH_QUEST_BADGES || {};
      /* R198: cap the strip at 5 (already rarity-sorted, rarest first) so it stays
         a single row; the 6th slot and beyond collapse into one +N chip. */
      var CAP = 5;
      var shown = heroBadges.slice(0, CAP);
      var extra = heroBadges.length - shown.length;
      var chips = shown.map(function(key){
        var b = reg[key]; if(!b) return '';
        var tip = b.name + (b.flavor ? '. ' + b.flavor : '');
        return '<span class="hoh-quest-chip hoh-rar-' + (b.rarity || 'standard') + '" aria-label="' + safeEsc(tip) + '" data-badge-key="' + safeEsc(key) + '">' + b.svg + '</span>';
      }).join('');
      if(extra > 0) chips += '<span class="hoh-quest-chip-more" aria-label="' + extra + ' more badges">+' + extra + '</span>';
      return '<div class="hoh-quest-row">' + chips + '</div>';
    } catch(_){ return ''; }
  };

  /* R175: badge tooltip rendered on document.body, not inside the card.
     The grid card and its portrait both use overflow:hidden for rounded
     corners, which clipped the in-card tooltip whenever it was taller than the
     remaining portrait space (the "cut off in the main hall" bug). A single
     shared layer positioned over the hovered chip via getBoundingClientRect
     can't be clipped by any ancestor. One delegated listener survives every
     gallery re-render. Touch has no hover, so a tap still opens the detail. */
  (function(){
    var tipEl = null;
    function ensureTip(){
      if(tipEl && document.body.contains(tipEl)) return tipEl;
      tipEl = document.createElement('div');
      tipEl.className = 'hoh-badge-tip';
      tipEl.setAttribute('role', 'tooltip');
      document.body.appendChild(tipEl);
      return tipEl;
    }
    function hideTip(){ if(tipEl) tipEl.classList.remove('hoh-badge-tip-show'); }
    function showFor(chip){
      var key = chip.getAttribute('data-badge-key');
      var reg = window.HOH_QUEST_BADGES || {};
      var b = key && reg[key];
      if(!b) return;
      var el = ensureTip();
      el.innerHTML = '';
      var n = document.createElement('span');
      n.className = 'hoh-badge-tip-name';
      n.textContent = b.name || '';
      el.appendChild(n);
      if(b.flavor){
        var f = document.createElement('span');
        f.className = 'hoh-badge-tip-flavor';
        f.textContent = b.flavor;
        el.appendChild(f);
      }
      /* Measure while still hidden (visibility:hidden keeps layout), then place
         it above the chip, right-aligned, clamped to the viewport, flipping
         below if there isn't room above. */
      var tr = el.getBoundingClientRect();
      var r = chip.getBoundingClientRect();
      var top = r.top - tr.height - 10;
      var left = r.right - tr.width;
      if(left < 8) left = 8;
      if(left + tr.width > window.innerWidth - 8) left = window.innerWidth - 8 - tr.width;
      if(top < 8) top = r.bottom + 10;
      el.style.top = Math.round(top) + 'px';
      el.style.left = Math.round(left) + 'px';
      el.classList.add('hoh-badge-tip-show');
    }
    document.addEventListener('mouseover', function(e){
      var t = e.target;
      var chip = t && t.closest ? t.closest('.hoh-quest-chip, .hoh-badge-cell') : null;
      if(chip && chip.classList.contains('hoh-quest-chip-more')) return;
      if(chip) showFor(chip);
    });
    document.addEventListener('mouseout', function(e){
      var t = e.target;
      var chip = t && t.closest ? t.closest('.hoh-quest-chip, .hoh-badge-cell') : null;
      if(chip) hideTip();
    });
    /* R415: passive. This handler only removes a class and never calls
       preventDefault, so telling the browser so up front removes the
       "wait for the listener before the first scroll frame" tax on touch
       scrolling. Capture is kept — the old `true` third arg meant capture,
       and nested scrollers still need to hide the tip. */
    window.addEventListener('scroll', hideTip, { capture: true, passive: true });
  })();

  /* Build one card. Returns HTML string identical to the section's
     previous inline template, with the ticket pill added inside
     hoh-card-footer next to the heart button. */
  window.hohBuildCardHtml = function(c, i, newId){
    if(!c || !c.id) return '';
    var hc = getHC(c.id);
    var h = hc > 0;
    var ht = htClass(hc);
    var nc = (c.id === newId) ? ' hoh-card-new' : '';
    var cardImgUrl = c.image_thumb_url || c.image_url;
    /* R204: animated heroes keep a still poster (the thumb) in the grid and
       swap to the animated full only while playing (hover on desktop, a few
       in-view on mobile). data-anim-full carries the source; the motion dot
       marks which heroes move. */
    var isAnimated = !!c.is_animated;
    var animFull = isAnimated ? String(c.image_url || '').replace(/"/g, '&quot;') : '';
    var motionDot = isAnimated ? '<span class="hoh-card-motion" title="Animated" aria-hidden="true"><svg viewBox="0 0 24 24" width="9" height="9" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></span>' : '';
    var firstLetter = safeEsc(c.name.charAt(0));
    /* Eager load + high fetch priority on the first batch to make
       above-the-fold images appear quickly on mobile. */
    var la = (i < 6) ? 'eager' : 'lazy';
    var fp = (i < 4) ? ' fetchpriority="high"' : '';
    /* Read the user's stored vertical crop offset for this hero so the
       card thumbnail honors the same reframing the owner picked in the
       edit form. Defaults to 50 (center) when unset. */
    var cardOffY = (typeof window.hohGetImageOffsetY === 'function')
      ? window.hohGetImageOffsetY(c.id, 'primary') : 50;
    /* R200: horizontal crop rides along for wide images. */
    var cardPos = (typeof window.hohImgObjPos === 'function')
      ? window.hohImgObjPos(c.id, 'primary') : ('50% ' + cardOffY + '%');
    var cardImgStyle = (typeof window.hohImgStyle === 'function')
      ? window.hohImgStyle(c.id, 'primary') : ('object-position:' + cardPos + ';');
    var im = cardImgUrl
      ? '<img src="' + cardImgUrl + '" alt="' + safeEsc(c.name) + '" loading="' + la + '"' + fp + ' decoding="async" crossorigin="anonymous" draggable="false" oncontextmenu="return false" data-letter="' + firstLetter + '" style="' + cardImgStyle + '" onerror="hohImgFallback(this)">'
      : '<div class="hoh-card-letter">' + firstLetter + '</div>';
    var fb = c.featured ? '<div class="hoh-featured-badge">Featured</div>' : '';
    var rar = rarityForHero(c);
    var isAnonHall = (typeof window.hohIsHallSignedIn === 'function')
      ? !window.hohIsHallSignedIn() : true;
    /* R224: the character page shows the STATIC D&D level (1-20) the player input, held in
       c.dnd_level. c.level is the My Hall XP / hall level (preserved separately, shown only
       in My Hall) and must NOT appear here. Stays hidden until dnd_level is backfilled. */
    var _dnd = parseInt(c.dnd_level, 10);
    var lv = (_dnd >= 1) ? ' &middot; Lvl ' + Math.min(20, _dnd) : '';
    var cp = c.campaign ? '<p class="hoh-card-campaign">' + safeEsc(c.campaign) + '</p>' : '';
    /* R140-Z3: FF logo click no longer opens the spotlight rules modal.
       It now activates a hidden "FF Portraits" sort that filters the
       grid to FF portraits only, ranked by most hearts. The sort
       option is added to the sort dropdown only when activated and
       removed when the user switches to any other sort — keeps the
       feature a discovery surface rather than permanent UI clutter. */
    /* BINDER-01 (Jan, 2026-10-23: "instead of the FF logo in the corner, do Jan's signature in the corner instead"). The
       mark keeps its class and its tap (the FF portraits sort, the tooltip); it is Jan's real signature now, masked from
       assets/ff-collector-signature.png (--ffb-sig, set by snippets/ff-binder.liquid) and painted in gold. */
    var ffBadge = c.is_ff_portrait
      ? '<span class="hoh-ff-badge hoh-ff-sig" role="button" tabindex="0" aria-label="FramedFantasy portrait, signed by Jan. Show every FramedFantasy portrait" onclick="event.stopPropagation();if(window.hohActivateFfPortraitsSort)hohActivateFfPortraitsSort(this);" onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();event.stopPropagation();if(window.hohActivateFfPortraitsSort)hohActivateFfPortraitsSort(this);}"><i></i></span>'
      : '';
    var heartIcon = h ? '&#9829;&#xFE0E;' : '&#9825;&#xFE0E;';
    var minePill = (hc > 1) ? '<span class="hoh-heart-mine">x' + fmtM(hc) + '</span>' : '';
    /* Featured class enables a subtle mythic-style rainbow glow on
       the whole card. The badge itself sits ABSOLUTELY positioned
       on the top edge of the image — centered horizontally with
       half above the image and half overlapping it, so it reads
       like a label tag pinned to the portrait. */
    var featuredCls = c.featured ? ' hoh-card-featured' : '';
    /* R46: god-mode visual marker for is_hidden characters. Non-god
       users never see hidden chars (filtered in loadCharacters), so
       this class only ever applies on Jangela\'s screen. */
    /* OWNHIDDEN (2026-10-29): the owner's grid shows their own hidden heroes too, marked as only theirs. */
    var hiddenCls = c.is_hidden ? ' hoh-card-hidden' + ((typeof window.hohIsOwnHiddenHero === 'function' && window.hohIsOwnHiddenHero(c)) ? ' hoh-card-ownhidden' : '') : '';
    /* R65: Mythic gating. At mythic tier the hero\'s heart count is
       HIDDEN and the heart button is LOCKED (won\'t accept hearts
       from this card-spam path). Hearts can still flow into a mythic
       via Explore the Hall (the wander-quest hearts in Fresh Faces),
       so the wander path is exempted by the gate inside hohHeart.
       Mythic class also enables the rainbow shimmer outline on the
       whole card. */
    var isMythic = (typeof window.hohIsMythicCharacter === 'function')
      ? window.hohIsMythicCharacter(c)
      : (c && (c.hearts || 0) >= 300);
    var mythicCls = isMythic ? ' hoh-card-mythic-locked' : '';
    /* R169: Legend Locked band (1000-1199 hearts since R769). Still Legendary tier, but the
       card heart is locked here; only Explore the Hall can feed them toward Mythic. */
    var isLegendLocked = !isMythic && (typeof window.hohIsLegendLocked === 'function') && window.hohIsLegendLocked(c);
    var legendLockedCls = isLegendLocked ? ' hoh-card-legend-locked' : '';
    /* R133-A: Capped-today gate. When this hero hit cap_rejected
       earlier today, mark the heart button disabled visually so the
       user can't even attempt to give one. The cap toast still fires
       on click via the pre-flight check inside hohHeart, but the
       visual disable is the primary signal. */
    /* R793: the daily heart cap and the cooldown belong to counted hearts;
       a favourite has neither, so the live Hall never dims a card for them. */
    var r793Card = (typeof window.hohR793Live === 'function') && window.hohR793Live();
    var isCappedToday = (!r793Card && typeof window.hohIsCharCappedToday === 'function')
      ? !!window.hohIsCharCappedToday(c.id) : false;
    var cappedCls = isCappedToday ? ' hoh-card-capped-today' : '';
    /* R166: Soft-cooldown visual. After a server cap rejection on this
       hero (not the user's personal cap — the legacy global cap miss-
       firing), the card dims for ~30s so the user understands why
       their click no-ops with a "paused" toast. Re-evaluated at render
       time AND toggled live by the document listener wired in
       hoh-section-core.js so cards rendered before the cooldown
       started catch up. */
    var isOnCooldown = (!r793Card && typeof window.hohIsCharOnCooldown === 'function')
      ? !!window.hohIsCharOnCooldown(c.id) : false;
    var cooldownCls = isOnCooldown ? ' hoh-card-on-cooldown' : '';
    var heartBtnHtml;
    /* R793 (Jan, 2026-09-22): hearts are a personal favourite now, one per
       account and hero, and no counts go up. Every card, signed in or not,
       shows a heart TOGGLE with no number (R793.1: and no rarity word). A
       signed-out tap asks to sign in (hohHeart -> hohFavToggle). The old
       count lives on only as the Legacy hearts badge in the opened view.
       The visible heart stays the footer's height; its 44px hit area is a
       pseudo-element in CSS, so no card grows. In LEGACY mode (the R793B
       doors not installed yet) the branches below are exactly today's. */
    /* R793.1 (Jan, 2026-09-22): "remove the rarity label from the main hall
       and keep the rarity label just to the border". The live footer is the
       name and the favourite toggle; the tier shows only as the card's
       hoh-card-rar-<tier> border. The toggle keeps the footer's 20px height,
       so no card changes size. */
    /* R793.1 fix: the live footer from the first paint, before the doors
       have answered (a first visit): hohR793Words is live OR not known yet.
       A Hall that turns out to be legacy repaints its footers
       (hohR793RepaintCardFooters on hoh:r793-mode). */
    var r793 = (typeof window.hohR793Words === 'function')
      ? !!window.hohR793Words()
      : ((typeof window.hohR793Live === 'function') && window.hohR793Live());
    if(r793){
      var favOn = (typeof window.hohFavIsOn === 'function') && window.hohFavIsOn(c.id);
      var favName = safeEsc(c.name);
      heartBtnHtml =
        /* the label says the state and the action, so no aria-pressed */
        '<button type="button" class="hoh-heart-btn hoh-fav-btn' + (favOn ? ' is-on hearted' : '') + '" data-fav-id="' + safeEsc(c.id) + '"' +
          ' aria-label="' + (favOn ? 'Remove ' + favName + ' from favorites' : 'Add ' + favName + ' to favorites') + '"' +
          ' onclick="event.stopPropagation();hohHeart(\'' + safeJsString(c.id) + '\',this)">' +
          '<span class="hoh-heart-icon" aria-hidden="true">' + (favOn ? '&#9829;&#xFE0E;' : '&#9825;&#xFE0E;') + '</span>' +
        '</button>';
    } else if(isAnonHall){
      /* Anonymous Hall cards teach the rarity ladder without exposing the
         heart economy. Every tier gets the same clean footer label; signed-in
         visitors continue through the existing interactive/count branches. */
      var rarityLabel = rar.label || (rar.tier.charAt(0).toUpperCase() + rar.tier.slice(1));
      heartBtnHtml =
        '<button class="hoh-heart-btn hoh-heart-btn-anon-rarity hoh-heart-btn-anon-rarity-' + rar.tier + '" disabled aria-label="' + safeEsc(rarityLabel) + ' rarity" onclick="event.stopPropagation();">' +
          '<span class="hoh-heart-count hoh-heart-count-anon-rarity hoh-heart-count-anon-rarity-' + rar.tier + '">' + safeEsc(rarityLabel) + '</span>' +
        '</button>';
    } else if(isMythic){
      /* R70: Drop the sparkle icon and the pill chrome — show just
         the word "Mythic" as clean rainbow text. The card border
         already shimmers in mythic colors; the badge doesn\'t need
         to repeat the visual weight. */
      heartBtnHtml =
        '<button class="hoh-heart-btn hoh-heart-btn-mythic-locked" disabled aria-label="Mythic. Heart this hero in Explore the Hall to keep feeding them" onclick="event.stopPropagation();" title="Mythic. Heart this hero in Explore the Hall to keep feeding them">' +
          '<span class="hoh-heart-count hoh-heart-count-mythic">Mythic</span>' +
        '</button>';
    } else if(isLegendLocked){
      /* R169: Legend Locked. Show the tier word in place of the heart count, no
         heart from the grid. The last 200 to Mythic come from Explore the Hall. */
      heartBtnHtml =
        '<button class="hoh-heart-btn hoh-heart-btn-legend-locked" disabled aria-label="Legend Locked. Heart this hero in Explore the Hall to push them to Mythic" onclick="event.stopPropagation();" title="Legend Locked. Heart this hero in Explore the Hall to push them to Mythic">' +
          '<span class="hoh-heart-count hoh-heart-count-legend">Legendary</span>' +
        '</button>';
    } else if(isCappedToday){
      /* R133-A: Capped today — disabled, click shows toast instead
         of firing the heart. Still uses hohHeart so the toast logic
         lives in one place; the pre-flight gate inside hohHeart
         handles the early return + toast. */
      heartBtnHtml =
        '<button class="hoh-heart-btn hoh-heart-btn-capped-today' + (h ? ' hearted ' + ht : '') + '" data-mine="' + hc + '" aria-label="Daily cap reached. Heart this hero in Explore the Hall instead" title="Capped today. Heart this hero in Explore the Hall instead" onclick="event.stopPropagation();hohHeart(\'' + c.id + '\',this)">' +
          '<span class="hoh-heart-icon">' + heartIcon + '</span>' +
          '<span class="hoh-heart-count">' + c.hearts + '</span>' +
          minePill +
        '</button>';
    } else {
      heartBtnHtml =
        '<button class="hoh-heart-btn' + (h ? ' hearted ' + ht : '') + '" data-mine="' + hc + '" onclick="event.stopPropagation();hohHeart(\'' + c.id + '\',this)">' +
          '<span class="hoh-heart-icon">' + heartIcon + '</span>' +
          '<span class="hoh-heart-count">' + c.hearts + '</span>' +
          minePill +
        '</button>';
    }
    /* R437 (Jan): "remove the badges from the main grid as well for logged in
       folks as well ... the badges should only show up in the opened view."
       The strip was already hidden from logged-out visitors (R197); this
       finishes the thought. A grid tile now carries name, race+class,
       campaign and author, and nothing else — the badge row was six icons
       of detail nobody can act on at grid scale, and it was the widest
       thing in the card body. The detail modal builds its own badge grid
       from its own markup (.hoh-badge-grid), so nothing is lost, it just
       moves to the screen where there is room to read it.
       Not calling the builder at all also skips hohGetHeroBadges +
       hohOrderBadges for every tile, which on a 1000 hero grid is real. */
    var badgeStrip = '';
    /* Long submitter names need a little more breathing room between the
       anonymous rarity label and the card edge. Keep this opt-in so the
       established footer alignment on ordinary names does not move. */
    /* Quest heroes can arrive without a name; a bare "by" reads as broken.
       Short enough for a 360px phone footer ("an anonymous adventurer" cut off). */
    var playerName = hohBylineName(c) || 'a hooded stranger';
    /* R793: the live footer is the anonymous footer's layout (ellipsized
       name) plus the heart, for everyone. */
    var anonFooter = isAnonHall || r793;
    var longPlayerFooterCls = (anonFooter && playerName.length > 14)
      ? ' hoh-card-footer-anon-long-player' : '';
    /* R558: only a card activated from the public #hoh-gallery gets the new
       card-first lightbox. The same renderer is also used by review clones and
       other secondary surfaces; those deliberately keep their established
       detailed-view destination. event.target===this prevents Enter/Space on
       the nested heart or FF-logo controls from also opening the parent card. */
    var openId = safeJsString(c.id);
    var openFromTile = "if(this.closest&&this.closest('#hoh-gallery')&&!document.body.classList.contains('hoh-page-mode-yours')){hohOpenHallCard('" + openId + "',this);}else{hohOpenDetail('" + openId + "');}";
    /* BINDER-01 (Jan, 2026-10-23: "make the little character grid in the hall also reflect that as well when they have the
       collector's version toggled on"): the owner's design, drawn by ff-binder.js (window.ffbCollectorTile); the tile keeps
       its open control, its Featured and New tags and its classes, so the grid treats it like any other card */
    var ccTile = (c.is_ff_portrait && typeof window.ffbCollectorTile === 'function') ? window.ffbCollectorTile(c, { img: cardImgUrl, style: cardImgStyle }) : '';
    if (ccTile) {
      return '<div class="hoh-card hoh-card-cc hoh-card-rar-' + rar.tier + nc + featuredCls + hiddenCls + '" data-hero-id="' + safeEsc(c.id) + '" onclick="' + openFromTile + '">' +
        '<button type="button" class="hoh-card-open-control" aria-label="Open ' + safeEsc(c.name) + ' card" onclick="event.stopPropagation();' + openFromTile + '"></button>' +
        fb + ccTile +
      '</div>';
    }
    return '<div class="hoh-card hoh-card-rar-' + rar.tier + nc + featuredCls + hiddenCls + mythicCls + legendLockedCls + cappedCls + cooldownCls + (isAnimated ? ' hoh-card-has-anim' : '') + '" data-hero-id="' + safeEsc(c.id) + '"' + (animFull ? ' data-anim-full="' + animFull + '"' : '') + ' onclick="' + openFromTile + '">' +
      '<button type="button" class="hoh-card-open-control" aria-label="Open ' + safeEsc(c.name) + ' card" onclick="event.stopPropagation();' + openFromTile + '"></button>' +
      fb +
      /* R259: the corner whisper — heroes submitted within the last 3 days
         wear a tiny glassy NEW pill (approved demo, variant A). The breathing
         gold dot + micro-caps live in css injected by hoh-section-core. */
      ((function(){
        try{
          if(!c.created_at) return '';
          var t = new Date(c.created_at).getTime();
          if(isNaN(t) || (Date.now() - t) > 3*24*60*60*1000) return '';
          return '<span class="hoh-newtag" aria-label="New this week"><i></i><b>New</b></span>';
        }catch(_){ return ''; }
      })()) +
      '<div class="hoh-card-img" style="background:' + plBg(c.name) + '">' + im +
        '<div class="hoh-card-gradient"></div>' + ffBadge + motionDot +
      '</div>' +
      '<div class="hoh-card-body">' +
        '<h3 class="hoh-card-name' + ((c.name||'').length>27?' hoh-card-name-xs':((c.name||'').length>18?' hoh-card-name-sm':'')) + '">' + safeEsc(c.name) + '</h3>' +
        '<p class="hoh-card-meta">' + safeEsc(c.race) + ' ' + safeEsc(c.character_class) + lv + '</p>' +
        cp +
        badgeStrip +
        '<div class="hoh-card-footer' + (anonFooter ? ' hoh-card-footer-anon' : '') + (r793 ? ' hoh-card-footer-fav' : '') + longPlayerFooterCls + '">' +
          '<span class="hoh-card-player" title="by ' + safeEsc(playerName) + '">by ' + safeEsc(playerName) + '</span>' +
          /* BINDER-01 (Jan, 2026-10-23: "instead of the heart icon, it can be whether or not they owned the card so remove
             the heart icon"): the heart button is gone from the grid; ff-binder.js marks the card in your binder here */
          '<span class="hoh-own-slot" data-own-slot></span>' +
        '</div>' +
      '</div>' +
    '</div>';
  };
})();

/* ============================================================
   R204 — GRID ANIMATION CONTROLLER
   Animated heroes show a still poster by default. The animated full is layered
   on top ONLY while playing, then unloaded (src removed) so the browser stops
   decoding — that keeps a grid of many heroes light. Desktop plays on hover;
   touch devices autoplay the few cards in view, hard-capped. Honors
   prefers-reduced-motion and the data-saver flag, and pauses everything when a
   detail card opens or the tab is hidden. Uses event delegation + a
   MutationObserver so it survives every grid re-render with zero per-card wiring.
   ============================================================ */
(function(){
  if(typeof window==='undefined'||typeof document==='undefined') return;
  var mqHover=window.matchMedia?window.matchMedia('(hover:hover) and (pointer:fine)'):null;
  var hoverCapable=mqHover?mqHover.matches:false;
  var mqReduce=window.matchMedia?window.matchMedia('(prefers-reduced-motion:reduce)'):null;
  function reduceMotion(){ return mqReduce?mqReduce.matches:false; }
  var saveData=false; try{ saveData=!!(navigator.connection&&navigator.connection.saveData); }catch(_){}
  var MAX_PLAYING=4;          /* never decode more than this many at once */
  var playing=[];

  function fullOf(card){ return (card&&card.getAttribute)?card.getAttribute('data-anim-full'):''; }
  function boxOf(card){ return (card&&card.querySelector)?card.querySelector('.hoh-card-img'):null; }

  function start(card){
    if(reduceMotion()) return;
    if(!card||card._animOn) return;
    var src=fullOf(card); if(!src) return;
    var box=boxOf(card); if(!box) return;
    if(playing.length>=MAX_PLAYING){
      if(hoverCapable){ var victim=playing[0]; if(victim&&victim!==card) stop(victim,true); }
      else { return; } /* touch: don't thrash — wait for a slot as cards scroll out */
    }
    card._animOn=true; playing.push(card);
    var base=box.querySelector('img:not(.hoh-ff-badge):not(.hoh-card-anim)');
    var ov=box.querySelector('.hoh-card-anim');
    if(!ov){
      ov=document.createElement('img');
      ov.className='hoh-card-anim';
      ov.alt=''; ov.setAttribute('aria-hidden','true'); ov.setAttribute('decoding','async');
      ov.setAttribute('crossorigin','anonymous'); ov.draggable=false;
      ov.addEventListener('load',function(){ if(card._animOn) ov.classList.add('hoh-card-anim-show'); });
      ov.addEventListener('error',function(){ stop(card,true); });
      box.appendChild(ov);
    }
    /* keep the crop in sync with the poster (owner may have re-framed live) */
    if(base&&base.style&&base.style.objectPosition) ov.style.objectPosition=base.style.objectPosition;
    if(ov.getAttribute('src')!==src){ ov.setAttribute('src',src); }
    else { ov.classList.add('hoh-card-anim-show'); }
  }

  function stop(card,hard){
    if(!card) return;
    var i=playing.indexOf(card); if(i!==-1) playing.splice(i,1);
    card._animOn=false;
    var box=boxOf(card); if(!box) return;
    var ov=box.querySelector('.hoh-card-anim'); if(!ov) return;
    ov.classList.remove('hoh-card-anim-show');
    /* drop the source so decoding stops; wait out the fade first on a soft stop */
    if(hard){ ov.removeAttribute('src'); }
    else { setTimeout(function(){ if(!card._animOn) ov.removeAttribute('src'); },420); }
  }

  window.hohStopAllCardAnim=function(){ playing.slice().forEach(function(c){ stop(c,true); }); };

  /* DESKTOP — hover to play (delegated, so it survives grid re-renders) */
  if(hoverCapable){
    document.addEventListener('mouseover',function(e){
      var card=(e.target&&e.target.closest)?e.target.closest('.hoh-card[data-anim-full]'):null;
      if(card) start(card);
    });
    document.addEventListener('mouseout',function(e){
      var card=(e.target&&e.target.closest)?e.target.closest('.hoh-card[data-anim-full]'):null;
      if(card&&(!e.relatedTarget||!card.contains(e.relatedTarget))) stop(card);
    });
  }

  /* TOUCH / no-hover — autoplay the few cards in view, capped */
  if(!hoverCapable && !saveData && 'IntersectionObserver' in window){
    var io=new IntersectionObserver(function(entries){
      entries.forEach(function(en){
        if(en.isIntersecting&&en.intersectionRatio>=0.6) start(en.target);
        else stop(en.target);
      });
    },{threshold:[0,0.6,1]});
    var seen=('WeakSet' in window)?new WeakSet():null;
    function scan(){
      var cards=document.querySelectorAll('.hoh-card[data-anim-full]');
      for(var i=0;i<cards.length;i++){
        var el=cards[i];
        if(seen){ if(seen.has(el)) continue; seen.add(el); }
        else { if(el._animObs) continue; el._animObs=true; }
        io.observe(el);
      }
    }
    if(document.readyState!=='loading') scan();
    document.addEventListener('DOMContentLoaded',scan);
    var gal=document.getElementById('hoh-gallery');
    /* PERF-13 (2026-08-06): a FLIP re-render fires a BURST of childList
       mutations and each one used to run scan() — a document-wide query —
       inside the same frame. One rAF coalesces the burst into a single scan;
       scan() is idempotent (WeakSet), so behavior is unchanged. */
    var scanRaf=0;
    function scheduleScan(){ if(scanRaf) return; scanRaf=requestAnimationFrame(function(){ scanRaf=0; scan(); }); }
    if(gal&&'MutationObserver' in window){ new MutationObserver(scheduleScan).observe(gal,{childList:true,subtree:true}); }
    setInterval(function(){ if(!document.hidden) scan(); },2500); /* fallback for cards rendered outside #hoh-gallery. R318: skip while the tab is backgrounded — animations are stopped then anyway (see below). */
  }

  /* Save CPU + battery when the tab is backgrounded. */
  document.addEventListener('visibilitychange',function(){ if(document.hidden && window.hohStopAllCardAnim) window.hohStopAllCardAnim(); });
})();
